import { useState } from 'react'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { sendFeedback } from '~/api/feedback'
import { errMessage } from '~/lib/errors'
import { cn } from '~/lib/utils'
import { Button } from '~/components/ui/button'
import type {
    FeedbackRequest,
    FeedbackService,
    FeedbackTargetType,
} from '~/api/types'

export interface FeedbackButtonsProps {
    service: FeedbackService
    targetType: FeedbackTargetType
    /** 메시지/세션/평가 id. 없으면 생략(백엔드 nullable). */
    targetId?: string
    className?: string
}

type Rating = FeedbackRequest['rating']

/**
 * 작고 절제된 사용자 평점 위젯.
 * 👍/👎 토글 후 POST /feedback. 👎면 짧은 코멘트를 한 번 더 받아 같이 보낸다.
 * 실패는 errMessage로 조용히 인라인 표시한다(레이아웃을 깨지 않음).
 */
export function FeedbackButtons({
    service,
    targetType,
    targetId,
    className,
}: FeedbackButtonsProps) {
    const [rating, setRating] = useState<Rating | null>(null)
    const [submitting, setSubmitting] = useState(false)
    // 👎 코멘트 입력 단계 여부와 전송 완료 여부.
    const [askComment, setAskComment] = useState(false)
    const [comment, setComment] = useState('')
    const [done, setDone] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const post = async (value: Rating, withComment?: string) => {
        setSubmitting(true)
        setError(null)
        try {
            await sendFeedback({
                service,
                targetType,
                targetId,
                rating: value,
                comment: withComment?.trim() || undefined,
            })
            return true
        } catch (err: unknown) {
            setError(errMessage(err, '평점을 보내지 못했어요.'))
            return false
        } finally {
            setSubmitting(false)
        }
    }

    const handleRate = async (value: Rating) => {
        if (submitting || done) return
        setRating(value)
        const ok = await post(value)
        if (!ok) {
            setRating(null)
            return
        }
        // 👍는 바로 완료, 👎는 짧은 코멘트를 선택 입력받는다.
        if (value === 1) setDone(true)
        else setAskComment(true)
    }

    const handleSendComment = async () => {
        if (submitting) return
        const ok = await post(-1, comment)
        if (ok) {
            setAskComment(false)
            setDone(true)
        }
    }

    const skipComment = () => {
        setAskComment(false)
        setDone(true)
    }

    if (done) {
        // 👍는 따뜻한 긍정 강화(마이크로 리워드)로, 👎는 개선 반영 프레이밍으로 차별화한다.
        // 긍정도 눌러볼 맛이 나게 해 부정 편향(불편할 때만 누름)을 완화한다.
        const positive = rating === 1
        return (
            <p
                className={cn(
                    'text-xs',
                    positive ? 'text-success' : 'text-muted-foreground',
                    className
                )}>
                {positive
                    ? '도움이 됐다니 기뻐요! 소중한 평가 감사합니다 🙂'
                    : '의견 감사합니다. 더 나아지도록 반영할게요.'}
            </p>
        )
    }

    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <div className='flex items-center gap-1'>
                <span className='text-muted-foreground mr-1 text-xs'>
                    답변이 도움이 됐나요?
                </span>
                <Button
                    type='button'
                    variant='ghost'
                    size='icon-sm'
                    aria-label='도움이 됐어요'
                    aria-pressed={rating === 1}
                    disabled={submitting}
                    onClick={() => void handleRate(1)}
                    className={cn(
                        'text-muted-foreground hover:text-foreground',
                        rating === 1 && 'text-success'
                    )}>
                    <ThumbsUp />
                </Button>
                <Button
                    type='button'
                    variant='ghost'
                    size='icon-sm'
                    aria-label='아쉬워요'
                    aria-pressed={rating === -1}
                    disabled={submitting}
                    onClick={() => void handleRate(-1)}
                    className={cn(
                        'text-muted-foreground hover:text-foreground',
                        rating === -1 && 'text-destructive'
                    )}>
                    <ThumbsDown />
                </Button>
            </div>

            {askComment ? (
                <div className='flex items-center gap-1.5'>
                    <input
                        type='text'
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder='어떤 점이 아쉬웠는지 알려주세요(선택)'
                        disabled={submitting}
                        className='border-input bg-card text-foreground placeholder:text-muted-foreground focus-visible:ring-ring h-8 w-full rounded-md border px-2.5 text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50'
                    />
                    <Button
                        type='button'
                        size='sm'
                        variant='secondary'
                        loading={submitting}
                        onClick={() => void handleSendComment()}>
                        보내기
                    </Button>
                    <Button
                        type='button'
                        size='sm'
                        variant='ghost'
                        disabled={submitting}
                        onClick={skipComment}
                        className='text-muted-foreground'>
                        건너뛰기
                    </Button>
                </div>
            ) : null}

            {error ? <p className='text-destructive text-xs'>{error}</p> : null}
        </div>
    )
}
